import { PageWidget } from '@components/PageWidget';
import { TabWidget } from '@components/TabWidget';
import { TabPayLink } from '@/components/TabPayLink';
import { useActiveTab } from '../../hooks';
import { useEffect, useState } from 'react';
import { Spinner } from '@components/Spinner';
import { formatNumber } from '@/utils/number';
import { TabStatusContainer, TabStatusText, TabPayButton } from './styled';
import { Table } from '@/Table';
import { getPurchasesTableColumns, byTabStatus } from '../../utils';
import { Purchase, Tab } from '../../types';
import { useQueryClient } from 'react-query';

function TabTab({ skipPaymentWindowCheck = false }) {
  const tabLimit = 500;
  const perPage = 6;
  const tableColumns = getPurchasesTableColumns();
  const { data: activeTabData, isLoading: isActiveTabLoading } = useActiveTab();
  const queryClient = useQueryClient();
  const [tabTotal, setTabTotal] = useState<number>(0);
  const [tabFull, setTabFull] = useState<Tab>();
  const [activeTabPurchases, setActiveTabPurchases] = useState<Purchase[]>([]);

  const onPaymentComplete = () => {
    queryClient.setQueryData(['tabs', 'active'], []);
  };

  useEffect(() => {
    if (activeTabData) {
      setTabTotal(
        activeTabData.reduce((prev: number, tab: Tab) => prev + tab.total, 0)
      );
      setActiveTabPurchases(
        activeTabData.reduce(
          (prev: Purchase[], tab: Tab) => prev.concat(tab.purchases),
          []
        )
      );
      setTabFull(activeTabData.find(byTabStatus('full')));
    }
  }, [activeTabData]);

  const sortedActivePurchases = activeTabPurchases.sort(
    (a: Purchase, b: Purchase) => {
      return (
        new Date(b.purchase_date).getTime() -
        new Date(a.purchase_date).getTime()
      );
    }
  );

  return (
    <>
      <PageWidget title='Tab Overview'>
        {!isActiveTabLoading ? (
          <TabStatusContainer>
            <TabWidget
              progress={(tabTotal * 100) / tabLimit}
              size={120}
              total={formatNumber({ number: tabTotal / 100 })}
            />
            <TabStatusText>
              You've used{' '}
              <span>{formatNumber({ number: tabTotal / 100 })}</span> of your{' '}
              <span>
                {formatNumber({
                  number: tabLimit / 100,
                  minimumFractionDigits: 0,
                })}
              </span>{' '}
              Tab.
            </TabStatusText>
            {tabFull && (
              <TabPayLink
                skipSameOrigin={skipPaymentWindowCheck}
                skipSameSource={skipPaymentWindowCheck}
                as={TabPayButton}
                tabId={tabFull.id}
                onComplete={onPaymentComplete}
              />
            )}
          </TabStatusContainer>
        ) : (
          <Spinner variant='small' />
        )}
      </PageWidget>
      <PageWidget title='Open Tab'>
        {!isActiveTabLoading ? (
          <>
            {sortedActivePurchases.length ? (
              <Table
                data={sortedActivePurchases}
                columns={tableColumns}
                metadata={{
                  count: sortedActivePurchases.length,
                  number_pages: Math.ceil(
                    sortedActivePurchases.length / perPage
                  ),
                  per_page: perPage,
                }}
                perPage={perPage}
                onPageChange={() => {}}
              />
            ) : (
              <p>Your tab is empty.</p>
            )}
          </>
        ) : (
          <Spinner />
        )}
      </PageWidget>
    </>
  );
}

export { TabTab };
